// src/pages/DisassemblyInstructionsPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader, AlertCircle, BookOpen, ChevronDown } from 'lucide-react';
import Navbar from '../navbar';

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

const getHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

function DisassemblyInstructionsPage() {
    const navigate = useNavigate();

    const [productGroups, setProductGroups]         = useState([]);
    const [selectedGroup, setSelectedGroup]         = useState('');
    const [groupsLoading, setGroupsLoading]         = useState(true);
    const [instructions, setInstructions]           = useState(null); // now array
    const [instructionsLoading, setInstructionsLoading] = useState(false);
    const [error, setError]                         = useState(null);

    // Role guard
    useEffect(() => {
        try {
            const token = localStorage.getItem('authToken') || localStorage.getItem('id_token');
            if (!token) { navigate('/dashboard'); return; }
            const decoded = JSON.parse(atob(token.split('.')[1]));
            const role = decoded.type || decoded.role || null;
            if (role !== 'Manufacturer' && role !== 'Repair/Refurb') {
                navigate('/dashboard');
            }
        } catch {
            navigate('/dashboard');
        }
    }, [navigate]);

    // Fetch product groups on mount
    useEffect(() => {
        const fetchProductGroups = async () => {
            try {
                setGroupsLoading(true);
                const response = await axios.get(`${API_BASE_URL}/product-groups`, {
                    headers: getHeaders(),
                });
                const groups = response.data?.data?.groups || [];
                setProductGroups(groups);
                if (groups.length > 0) {
                    setSelectedGroup(groups[0].productName);
                }
            } catch (err) {
                console.error('Failed to fetch product groups:', err);
                setError('Failed to load product groups. Please refresh the page.');
            } finally {
                setGroupsLoading(false);
            }
        };
        fetchProductGroups();
    }, []);

    // Fetch instructions when product group changes
    useEffect(() => {
        if (!selectedGroup) { setInstructions(null); return; }
        const fetchInstructions = async () => {
            try {
                setInstructionsLoading(true);
                setError(null);
                setInstructions(null);
                const response = await axios.get(
                    `${API_BASE_URL}/disassembly-instructions/${encodeURIComponent(selectedGroup)}`,
                    { headers: getHeaders() }
                );
                if (response.data?.success && response.data?.data?.length > 0) {
                    setInstructions(response.data.data); // array
                } else {
                    setInstructions(null);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setInstructions(null);
                } else {
                    console.error('Failed to fetch instructions:', err);
                    setError('Failed to load instructions. Please try again.');
                }
            } finally {
                setInstructionsLoading(false);
            }
        };
        fetchInstructions();
    }, [selectedGroup]);

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
            <Navbar />

            {/* Top Bar */}
            <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-0 mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl overflow-hidden shadow">
                <button
                    onClick={() => navigate(-1)}
                    className="flex items-center text-blue-600 hover:text-blue-700 text-sm sm:text-base"
                >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 mr-1 sm:mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                    Back
                </button>
                <span className="text-blue-600 font-medium text-sm sm:text-base">
                    Disassembly Instructions
                </span>
            </div>

            {/* Main Content */}
            <div className="p-4 sm:p-6 lg:p-8">
                <div className="bg-white rounded-lg shadow-md p-4 sm:p-6 lg:p-8">

                    {/* Page Title + Group Selector */}
                    <div className="mb-6 pb-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                                <BookOpen className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <h1 className="text-xl font-semibold text-gray-800">Disassembly Instructions</h1>
                                <p className="text-xs text-gray-500 mt-0.5">View uploaded PDF instructions per product group</p>
                            </div>
                        </div>

                        {/* Product Group Selector */}
                        <div className="relative min-w-[220px]">
                            <select
                                value={selectedGroup}
                                onChange={(e) => setSelectedGroup(e.target.value)}
                                disabled={groupsLoading || instructionsLoading}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white appearance-none pr-8 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {groupsLoading ? (
                                    <option>Loading...</option>
                                ) : productGroups.length === 0 ? (
                                    <option value="">No product groups found</option>
                                ) : (
                                    <>
                                        <option value="">Select product group</option>
                                        {productGroups.map((group) => (
                                            <option key={group.id} value={group.productName}>
                                                {group.productName}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                            <ChevronDown className="w-4 h-4 text-gray-500 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                        </div>
                    </div>

                    {/* Error */}
                    {error && (
                        <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-lg p-4">
                            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                            <p className="text-sm text-red-700">{error}</p>
                        </div>
                    )}

                    {/* Loading */}
                    {!error && instructionsLoading && (
                        <div className="flex items-center justify-center py-16 gap-3">
                            <Loader className="w-5 h-5 animate-spin text-blue-500" />
                            <span className="text-sm text-gray-500">Loading instructions...</span>
                        </div>
                    )}

                    {/* No group selected */}
                    {!error && !instructionsLoading && !selectedGroup && (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <BookOpen className="w-10 h-10 text-gray-300 mb-3" />
                            <p className="text-sm text-gray-500">Select a product group to view its disassembly instructions.</p>
                        </div>
                    )}

                    {/* No instructions found */}
                    {!error && !instructionsLoading && selectedGroup && !instructions && (
                        <div className="flex flex-col items-center justify-center py-16 text-center">
                            <BookOpen className="w-10 h-10 text-gray-300 mb-3" />
                            <p className="text-sm font-medium text-gray-600">No instructions uploaded yet</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Upload a PDF from the dashboard to add instructions for{' '}
                                <span className="font-medium">{selectedGroup}</span>.
                            </p>
                        </div>
                    )}

                    {/* Instructions list — one card per PDF */}
                    {!error && !instructionsLoading && instructions && (
                        <div className="space-y-6">
                            <p className="text-xs text-gray-500">
                                <span className="font-medium text-gray-700">{instructions.length}</span> PDF{instructions.length !== 1 ? 's' : ''} uploaded for <span className="font-medium text-gray-700">{selectedGroup}</span>
                            </p>
                            {instructions.map((item, index) => (
                                <div key={item.id} className="border border-gray-200 rounded-lg p-5">
                                    {/* PDF meta */}
                                    <div className="flex flex-wrap gap-4 mb-4 text-xs text-gray-500 pb-3 border-b border-gray-100">
                                        <span className="flex items-center gap-1">
                                            <BookOpen className="w-3.5 h-3.5 text-blue-500" />
                                            <span className="font-medium text-blue-600">
                                                {item.pdf_filename || `Document ${index + 1}`}
                                            </span>
                                        </span>
                                        {item.uploaded_at && (
                                            <span>
                                                <span className="font-medium text-gray-700">Uploaded:</span>{' '}
                                                {new Date(item.uploaded_at).toLocaleDateString('en-GB', {
                                                    day: 'numeric', month: 'long', year: 'numeric',
                                                })}
                                            </span>
                                        )}
                                        {item.uploaded_by && (
                                            <span>
                                                <span className="font-medium text-gray-700">By:</span>{' '}
                                                {item.uploaded_by}
                                            </span>
                                        )}
                                    </div>

                                    {/* Instructions text */}
                                    <div className="space-y-2">
                                        {(item.instructions_text || item.instructions || '')
                                            .split('\n')
                                            .filter((line) => line.trim())
                                            .map((line, i) => {
                                                const trimmed = line.trim();
                                                const isStep    = /^step\s+\d+/i.test(trimmed);
                                                const isBullet  = trimmed.startsWith('-');
                                                const isHeading = /^(safety notes|warning|note|tools required)/i.test(trimmed);
                                                return (
                                                    <p key={i} className={`text-sm leading-relaxed ${
                                                        isHeading ? 'font-semibold text-gray-800 mt-4'
                                                        : isStep   ? 'text-gray-700 mt-2'
                                                        : isBullet ? 'text-gray-600 ml-4'
                                                        : 'text-gray-500 text-xs mt-2'
                                                    }`}>
                                                        {trimmed}
                                                    </p>
                                                );
                                            })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default DisassemblyInstructionsPage;